/** @format */

import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowLeftRight,
  Check,
  ChevronDown,
  ChevronUp,
  GitCompare,
  Loader,
  Loader2,
  Search,
} from "lucide-react";
import Header from "../../components/ui/Header";
import SopAccessDenied from "../../components/sop/SopAccessDenied";
import { getVersion } from "../../api/sopVersions";
import { useSopStore } from "../../store/useSopStore";
import type { SOP, SOPVersion, VersionListItem } from "../../types/sop";
import { ProcessingStatusEnum } from "../../types/sop";
import { compareObjects } from "../../utils/sopDiff";
import type { DiffResult } from "../../utils/sopDiff";
import type { Step } from "../../types/sop";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import { extractImageIdsFromHtml, getImageUrls } from "../../utils/imageReferences";
import { diffWords } from "diff";
import parse, { domToReact } from "html-react-parser";
import {
  isPermissionDeniedError,
  PERMISSION_DENIED_MESSAGE,
} from "../../api/sopErrors";

/** Stable empty array for versionList selector to avoid new [] reference and re-render loops. */
const EMPTY_VERSION_LIST: VersionListItem[] = [];

type VersionOption = {
  versionId: string;
  label: string;
  dateText: string | null;
  badges: Array<"latest" | "draft" | "original">;
};

type VersionSearchSelectProps = {
  tone: "amber" | "emerald";
  value: string;
  options: VersionOption[];
  disabled?: boolean;
  loading?: boolean;
  onSelect: (versionId: string) => void;
};

const toneStyles = {
  amber: {
    triggerBorder: "border-amber-500/30 hover:border-amber-500/60 focus-within:border-amber-500",
    triggerRing: "focus-within:ring-2 focus-within:ring-amber-500/30",
    activeOption: "border-amber-500/40 bg-amber-500/10",
    spinner: "text-amber-400",
    check: "text-amber-300",
  },
  emerald: {
    triggerBorder: "border-emerald-500/30 hover:border-emerald-500/60 focus-within:border-emerald-500",
    triggerRing: "focus-within:ring-2 focus-within:ring-emerald-500/30",
    activeOption: "border-emerald-500/40 bg-emerald-500/10",
    spinner: "text-emerald-400",
    check: "text-emerald-300",
  },
} as const;

const badgeStyles: Record<VersionOption["badges"][number], string> = {
  latest: "border-blue-400/40 bg-blue-500/15 text-blue-200",
  draft: "border-fuchsia-400/40 bg-fuchsia-500/15 text-fuchsia-200",
  original: "border-slate-500/50 bg-slate-600/20 text-slate-200",
};

function VersionSearchSelect({
  tone,
  value,
  options,
  disabled = false,
  loading = false,
  onSelect,
}: VersionSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const styles = toneStyles[tone];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((option) => option.versionId === value) ?? null;

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((option) => {
      const searchableBadges = option.badges.join(" ");
      return `${option.label} ${option.dateText ?? ""} ${searchableBadges}`
        .toLowerCase()
        .includes(normalized);
    });
  }, [options, query]);

  const handlePick = (versionId: string) => {
    onSelect(versionId);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || loading}
        className={`w-full text-left rounded-lg border bg-slate-800/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles.triggerBorder} ${styles.triggerRing}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="px-3 py-2.5 pr-9 min-h-[44px]">
          {selected ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{selected.label}</p>
                {selected.dateText && (
                  <p className="text-xs text-slate-400 truncate">{selected.dateText}</p>
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-1">
                {selected.badges.map((badge) => (
                  <span
                    key={badge}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeStyles[badge]}`}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <span className="text-sm text-slate-400">Select a version...</span>
          )}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
          {loading ? (
            <Loader2 className={`w-3.5 h-3.5 animate-spin ${styles.spinner}`} />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {open && !disabled && !loading && (
        <div className="absolute z-[120] mt-2 w-full rounded-xl border border-slate-700 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/60">
          <div className="p-2 border-b border-slate-700/70">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search versions..."
                className="w-full rounded-md border border-slate-700 bg-slate-800/80 text-sm text-white placeholder:text-slate-500 pl-8 pr-2.5 py-2 outline-none focus:border-slate-500"
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto overscroll-contain p-1.5 space-y-1">
              {filteredOptions.length === 0 ? (
                <p className="px-2 py-3 text-xs text-slate-400">No versions found.</p>
              ) : (
                filteredOptions.map((option) => {
                  const isActive = option.versionId === value;
                  return (
                    <button
                      key={option.versionId}
                      type="button"
                      onClick={() => handlePick(option.versionId)}
                      className={`w-full text-left rounded-lg border border-transparent px-2.5 py-2 transition-colors hover:bg-slate-800/80 ${isActive ? styles.activeOption : ""}`}
                      role="option"
                      aria-selected={isActive}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{option.label}</p>
                          {option.dateText && (
                            <p className="text-xs text-slate-400 truncate">{option.dateText}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {option.badges.map((badge) => (
                            <span
                              key={badge}
                              className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeStyles[badge]}`}
                            >
                              {badge}
                            </span>
                          ))}
                          {isActive && (
                            <Check className={`w-3.5 h-3.5 ml-1 ${styles.check}`} />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function stripHtml(html: string): string {
  if (!html?.trim()) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeImageId(imageId: string): string {
  const trimmed = imageId.trim();
  return trimmed.startsWith("image://") ? trimmed.slice("image://".length) : trimmed;
}

function decodeSerializedHtml(html: string): string {
  if (!html) return "";
  const value = html.trim();
  // Some payloads arrive JSON-stringified (escaped quotes/newlines).
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "string") return parsed;
    } catch {
      // Fallback below when payload isn't valid JSON string.
    }
  }
  return value
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t");
}

function htmlToDiffText(html: string): string {
  const decodedHtml = decodeSerializedHtml(html);
  if (!decodedHtml?.trim()) return "";
  const withImageMarkers = decodedHtml.replace(/<img\b[^>]*>/gi, (imgTag) => {
    const idMatch = imgTag.match(/data-image-id\s*=\s*["']([^"']+)["']/i);
    if (!idMatch?.[1]) return " [image] ";
    const id = normalizeImageId(idMatch[1]);
    const shortId = id.length > 8 ? id.slice(0, 8) : id;
    return ` [image:${shortId}] `;
  });
  return stripHtml(withImageMarkers);
}

function normalizeHtmlIgnoringClass(html: string): string {
  const decodedHtml = decodeSerializedHtml(html);
  if (!decodedHtml?.trim()) return "";
  if (typeof DOMParser === "undefined") {
    return decodedHtml
      .replace(/(<img\b[^>]*?)\s+src\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "$1")
      .replace(/\s(class|style)\s*=\s*("([^"]*)"|'([^']*)')/gi, "")
      .replace(/>\s+</g, "><")
      .replace(/\s+/g, " ")
      .trim();
  }

  const doc = new DOMParser().parseFromString(`<div>${decodedHtml}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return "";

  const unwrapElement = (element: Element) => {
    const parent = element.parentNode;
    if (!parent) return;
    while (element.firstChild) parent.insertBefore(element.firstChild, element);
    parent.removeChild(element);
  };

  root.querySelectorAll("*").forEach((node) => {
    const tag = node.tagName.toLowerCase();

    // Ignore editor-injected visual attributes during semantic comparisons.
    node.removeAttribute("class");
    node.removeAttribute("style");
    node.removeAttribute("id");

    if (tag === "img") {
      const rawId = node.getAttribute("data-image-id") ?? "";
      const id = normalizeImageId(rawId);
      const width = node.getAttribute("width");
      const height = node.getAttribute("height");
      const normalizedWidth = width && Number.isFinite(Number(width)) ? String(Number(width)) : null;
      const normalizedHeight = height && Number.isFinite(Number(height)) ? String(Number(height)) : null;

      // Keep only stable attrs for images; src/isavatar signatures are volatile.
      Array.from(node.attributes).forEach((attr) => node.removeAttribute(attr.name));
      if (!id) {
        node.remove();
        return;
      }
      node.setAttribute("data-image-id", id);
      if (normalizedWidth) node.setAttribute("width", normalizedWidth);
      if (normalizedHeight) node.setAttribute("height", normalizedHeight);
      return;
    }

    // Span wrappers (like pdf-hidden annotations) should not change semantic diff.
    if (tag === "span") {
      unwrapElement(node);
      return;
    }

    // Editor often wraps list item content in <p>; treat as equivalent.
    if (tag === "p" && node.parentElement?.tagName.toLowerCase() === "li") {
      unwrapElement(node);
    }
  });

  root.querySelectorAll("p").forEach((node) => {
    if (!node.textContent?.trim() && !node.querySelector("img,br")) node.remove();
  });

  return root.innerHTML
    .replace(/<br\s*\/?>/gi, "<br>")
    // Ignore inconsequential whitespace before closing tags.
    .replace(/\s+(<\/[a-z0-9-]+>)/gi, "$1")
    .replace(/>\s+</g, "><")
    .replace(/\s+/g, " ")
    .trim();
}

type ImageSizeMeta = {
  width: number | null;
  height: number | null;
};

type ImageEntry = {
  key: string;
  id: string;
  width: number | null;
  height: number | null;
  anchor: string;
  index: number;
};

type ImageDiffMeta = {
  status: "unchanged" | "added" | "removed" | "resized";
  before?: ImageSizeMeta;
  after?: ImageSizeMeta;
};

type TextBlockEntry = {
  key: string;
  kind: "li" | "p";
  text: string;
  signature: string;
  inlineFingerprint: string;
};

type TextBlockDiffMeta = {
  status: "unchanged" | "added" | "removed" | "updated";
};

type CodeDiffMeta = {
  status: "unchanged" | "added" | "removed" | "updated";
};

type StepPart = Step["parts"][number];
type PartChange = {
  index: number;
  status: "unchanged" | "added" | "removed" | "updated";
  basePart: StepPart | null;
  comparePart: StepPart | null;
};

function extractImageEntriesFromHtml(html: string): ImageEntry[] {
  const decodedHtml = decodeSerializedHtml(html);
  if (!decodedHtml?.trim()) return [];
  const entries: ImageEntry[] = [];
  const imageCounts: Record<string, number> = {};
  const imgTagRegex = /<img\b[^>]*>/gi;
  const matches = Array.from(decodedHtml.matchAll(imgTagRegex));
  matches.forEach((match) => {
    const imgTag = match[0];
    const idx = match.index ?? 0;
    const idMatch = imgTag.match(/data-image-id\s*=\s*["']([^"']+)["']/i);
    const rawId = idMatch?.[1] ?? "";
    const id = normalizeImageId(rawId);
    const occurrence = imageCounts[id] ?? 0;
    imageCounts[id] = occurrence + 1;
    const widthMatch = imgTag.match(/width\s*=\s*["']?(\d+)/i);
    const heightMatch = imgTag.match(/height\s*=\s*["']?(\d+)/i);
    const width = widthMatch?.[1] ? Number(widthMatch[1]) : null;
    const height = heightMatch?.[1] ? Number(heightMatch[1]) : null;
    const before = stripHtml(decodedHtml.slice(Math.max(0, idx - 180), idx))
      .split(" ")
      .slice(-8)
      .join(" ");
    const after = stripHtml(decodedHtml.slice(idx + imgTag.length, idx + imgTag.length + 180))
      .split(" ")
      .slice(0, 8)
      .join(" ");
    entries.push({
      key: `${id}::${occurrence}`,
      id,
      width,
      height,
      anchor: `${before}|${after}`,
      index: idx,
    });
  });
  return entries;
}

function scoreAnchorSimilarity(a: string, b: string): number {
  if (!a && !b) return 1;
  const aTokens = new Set(a.toLowerCase().split(/\s+/).filter(Boolean));
  const bTokens = new Set(b.toLowerCase().split(/\s+/).filter(Boolean));
  if (!aTokens.size && !bTokens.size) return 1;
  let intersection = 0;
  aTokens.forEach((token) => {
    if (bTokens.has(token)) intersection += 1;
  });
  const union = aTokens.size + bTokens.size - intersection;
  return union > 0 ? intersection / union : 0;
}

function buildImageDiffMeta(
  baseHtml: string,
  compareHtml: string,
): {
  baseMetaByKey: Record<string, ImageDiffMeta>;
  compareMetaByKey: Record<string, ImageDiffMeta>;
} {
  const baseEntries = extractImageEntriesFromHtml(baseHtml);
  const compareEntries = extractImageEntriesFromHtml(compareHtml);
  const baseMetaByKey: Record<string, ImageDiffMeta> = {};
  const compareMetaByKey: Record<string, ImageDiffMeta> = {};

  const compareById = new Map<string, ImageEntry[]>();
  compareEntries.forEach((entry) => {
    const list = compareById.get(entry.id) ?? [];
    list.push(entry);
    compareById.set(entry.id, list);
  });

  baseEntries.forEach((baseEntry) => {
    const candidates = compareById.get(baseEntry.id) ?? [];
    if (!candidates.length) {
      baseMetaByKey[baseEntry.key] = {
        status: "removed",
        before: { width: baseEntry.width, height: baseEntry.height },
      };
      return;
    }
    let bestIdx = 0;
    let bestScore = Number.NEGATIVE_INFINITY;
    candidates.forEach((candidate, idx) => {
      const anchorScore = scoreAnchorSimilarity(baseEntry.anchor, candidate.anchor);
      const distancePenalty = Math.abs(baseEntry.index - candidate.index) / 100000;
      const score = anchorScore - distancePenalty;
      if (score > bestScore) {
        bestScore = score;
        bestIdx = idx;
      }
    });
    const matched = candidates.splice(bestIdx, 1)[0];
    const before = { width: baseEntry.width, height: baseEntry.height };
    const after = { width: matched.width, height: matched.height };
    const resized = before.width !== after.width || before.height !== after.height;
    baseMetaByKey[baseEntry.key] = { status: resized ? "resized" : "unchanged", before, after };
    compareMetaByKey[matched.key] = { status: resized ? "resized" : "unchanged", before, after };
  });

  compareById.forEach((remaining) => {
    remaining.forEach((entry) => {
      compareMetaByKey[entry.key] = {
        status: "added",
        after: { width: entry.width, height: entry.height },
      };
    });
  });

  return { baseMetaByKey, compareMetaByKey };
}

function extractTextBlockEntries(html: string): TextBlockEntry[] {
  const decodedHtml = decodeSerializedHtml(html);
  if (!decodedHtml?.trim() || typeof DOMParser === "undefined") return [];
  const doc = new DOMParser().parseFromString(`<div>${decodedHtml}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return [];

  const entries: TextBlockEntry[] = [];
  const counts: Record<string, number> = {};
  const inlineFingerprint = (snippet: string): string => {
    const tags = ["strong", "b", "em", "i", "u", "a", "code"] as const;
    return tags
      .map((tag) => {
        const regex = new RegExp(`<${tag}\\b`, "gi");
        const count = (snippet.match(regex) ?? []).length;
        return `${tag}:${count}`;
      })
      .join("|");
  };

  root.querySelectorAll("li, p").forEach((node) => {
    const kind = node.tagName.toLowerCase() as "li" | "p";
    // Ignore any <p> inside <li>; checklist/task-list markup often nests wrappers.
    if (kind === "p" && node.closest("li")) return;
    const occurrence = counts[kind] ?? 0;
    counts[kind] = occurrence + 1;
    const text = stripHtml(node.innerHTML);
    if (!text) return;
    const normalizedSnippet = normalizeHtmlIgnoringClass(
      kind === "li" ? node.outerHTML : node.innerHTML,
    );
    entries.push({
      key: `${kind}::${occurrence}`,
      kind,
      text,
      // For list items, normalize full node so <li><p>...</p></li> and <li>...</li> are equivalent.
      signature: normalizedSnippet,
      inlineFingerprint: inlineFingerprint(normalizedSnippet),
    });
  });

  return entries;
}

function buildTextBlockDiffMeta(
  baseHtml: string,
  compareHtml: string,
): {
  baseMetaByKey: Record<string, TextBlockDiffMeta>;
  compareMetaByKey: Record<string, TextBlockDiffMeta>;
} {
  const baseEntries = extractTextBlockEntries(baseHtml);
  const compareEntries = extractTextBlockEntries(compareHtml);
  const baseMetaByKey: Record<string, TextBlockDiffMeta> = {};
  const compareMetaByKey: Record<string, TextBlockDiffMeta> = {};

  const computeLcsPairs = (a: TextBlockEntry[], b: TextBlockEntry[]): Array<[number, number]> => {
    const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
      Array.from({ length: b.length + 1 }, () => 0),
    );

    for (let i = a.length - 1; i >= 0; i--) {
      for (let j = b.length - 1; j >= 0; j--) {
        if (a[i].signature === b[j].signature) {
          dp[i][j] = dp[i + 1][j + 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
      }
    }

    const pairs: Array<[number, number]> = [];
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
      if (a[i].signature === b[j].signature) {
        pairs.push([i, j]);
        i += 1;
        j += 1;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        i += 1;
      } else {
        j += 1;
      }
    }
    return pairs;
  };

  const textSimilarity = (a: string, b: string): number => {
    const aTokens = new Set(a.toLowerCase().split(/\s+/).filter(Boolean));
    const bTokens = new Set(b.toLowerCase().split(/\s+/).filter(Boolean));
    if (!aTokens.size && !bTokens.size) return 1;
    let intersection = 0;
    aTokens.forEach((token) => {
      if (bTokens.has(token)) intersection += 1;
    });
    const union = aTokens.size + bTokens.size - intersection;
    return union > 0 ? intersection / union : 0;
  };

  (["li", "p"] as const).forEach((kind) => {
    const baseOfKind = baseEntries.filter((e) => e.kind === kind);
    const compareOfKind = compareEntries.filter((e) => e.kind === kind);
    const matchedPairs = computeLcsPairs(baseOfKind, compareOfKind);
    const matchedBase = new Set<number>();
    const matchedCompare = new Set<number>();

    matchedPairs.forEach(([baseIdx, compareIdx]) => {
      const baseEntry = baseOfKind[baseIdx];
      const compareEntry = compareOfKind[compareIdx];
      matchedBase.add(baseIdx);
      matchedCompare.add(compareIdx);
      baseMetaByKey[baseEntry.key] = { status: "unchanged" };
      compareMetaByKey[compareEntry.key] = { status: "unchanged" };
    });

    const unmatchedBase = baseOfKind
      .map((entry, idx) => ({ entry, idx }))
      .filter(({ idx }) => !matchedBase.has(idx));
    const unmatchedCompare = compareOfKind
      .map((entry, idx) => ({ entry, idx }))
      .filter(({ idx }) => !matchedCompare.has(idx));

    const scoredPairs: Array<{ baseIdx: number; compareIdx: number; score: number }> = [];
    for (let baseIdx = 0; baseIdx < unmatchedBase.length; baseIdx++) {
      for (let compareIdx = 0; compareIdx < unmatchedCompare.length; compareIdx++) {
        const score = textSimilarity(
          unmatchedBase[baseIdx].entry.text,
          unmatchedCompare[compareIdx].entry.text,
        );
        scoredPairs.push({ baseIdx, compareIdx, score });
      }
    }
    scoredPairs.sort((a, b) => b.score - a.score);

    const usedBase = new Set<number>();
    const usedCompare = new Set<number>();
    scoredPairs.forEach(({ baseIdx, compareIdx, score }) => {
      if (score < 0.55) return;
      if (usedBase.has(baseIdx) || usedCompare.has(compareIdx)) return;
      usedBase.add(baseIdx);
      usedCompare.add(compareIdx);
      const baseEntry = unmatchedBase[baseIdx].entry;
      const compareEntry = unmatchedCompare[compareIdx].entry;
      const sameText = baseEntry.text === compareEntry.text;
      const sameInline = baseEntry.inlineFingerprint === compareEntry.inlineFingerprint;
      if (sameText && sameInline) {
        baseMetaByKey[baseEntry.key] = { status: "unchanged" };
        compareMetaByKey[compareEntry.key] = { status: "unchanged" };
      } else {
        baseMetaByKey[baseEntry.key] = { status: "updated" };
        compareMetaByKey[compareEntry.key] = { status: "updated" };
      }
    });

    unmatchedBase.forEach(({ entry }, idx) => {
      if (usedBase.has(idx)) return;
      baseMetaByKey[entry.key] = { status: "removed" };
    });
    unmatchedCompare.forEach(({ entry }, idx) => {
      if (usedCompare.has(idx)) return;
      compareMetaByKey[entry.key] = { status: "added" };
    });
  });

  return { baseMetaByKey, compareMetaByKey };
}

function extractCodeEntries(html: string): { key: string; signature: string }[] {
  const decodedHtml = decodeSerializedHtml(html);
  if (!decodedHtml?.trim() || typeof DOMParser === "undefined") return [];
  const doc = new DOMParser().parseFromString(`<div>${decodedHtml}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return [];
  const out: { key: string; signature: string }[] = [];
  let idx = 0;
  root.querySelectorAll("code").forEach((node) => {
    const signature = normalizeHtmlIgnoringClass(node.innerHTML);
    out.push({ key: `code::${idx++}`, signature });
  });
  return out;
}

function buildCodeDiffMeta(
  baseHtml: string,
  compareHtml: string,
): { baseMetaByKey: Record<string, CodeDiffMeta>; compareMetaByKey: Record<string, CodeDiffMeta> } {
  const baseEntries = extractCodeEntries(baseHtml);
  const compareEntries = extractCodeEntries(compareHtml);
  const baseMetaByKey: Record<string, CodeDiffMeta> = {};
  const compareMetaByKey: Record<string, CodeDiffMeta> = {};
  const max = Math.max(baseEntries.length, compareEntries.length);

  for (let i = 0; i < max; i++) {
    const baseEntry = baseEntries[i];
    const compareEntry = compareEntries[i];
    if (baseEntry && compareEntry) {
      const same = baseEntry.signature === compareEntry.signature;
      baseMetaByKey[baseEntry.key] = { status: same ? "unchanged" : "updated" };
      compareMetaByKey[compareEntry.key] = { status: same ? "unchanged" : "updated" };
    } else if (baseEntry) {
      baseMetaByKey[baseEntry.key] = { status: "removed" };
    } else if (compareEntry) {
      compareMetaByKey[compareEntry.key] = { status: "added" };
    }
  }

  return { baseMetaByKey, compareMetaByKey };
}

function partName(part: StepPart | null): string {
  return (part?.name ?? "—").trim() || "—";
}

function partUnits(part: StepPart | null): string {
  if (!part) return "—";
  return Number.isFinite(part.quantity) ? String(part.quantity) : "—";
}

function buildPartChanges(baseStep: Step | null, compareStep: Step | null): PartChange[] {
  const baseParts = baseStep?.parts ?? [];
  const compareParts = compareStep?.parts ?? [];
  const max = Math.max(baseParts.length, compareParts.length);
  const rows: PartChange[] = [];
  for (let i = 0; i < max; i++) {
    const basePart = baseParts[i] ?? null;
    const comparePart = compareParts[i] ?? null;
    if (basePart && comparePart) {
      const sameName = (basePart.name ?? "").trim() === (comparePart.name ?? "").trim();
      const sameQty = basePart.quantity === comparePart.quantity;
      rows.push({
        index: i,
        status: sameName && sameQty ? "unchanged" : "updated",
        basePart,
        comparePart,
      });
    } else if (basePart && !comparePart) {
      rows.push({ index: i, status: "removed", basePart, comparePart: null });
    } else if (!basePart && comparePart) {
      rows.push({ index: i, status: "added", basePart: null, comparePart });
    }
  }
  return rows;
}

function formatDimensionsLabel(meta: ImageSizeMeta | undefined): string | null {
  if (!meta) return null;
  if (meta.width == null && meta.height == null) return null;
  return `${meta.width ?? "auto"}x${meta.height ?? "auto"}`;
}

function renderStepHtmlWithImageDiff({
  html,
  side,
  textMetaByKey,
  codeMetaByKey,
  imageMetaByKey,
  imageUrls,
}: {
  html: string;
  side: "old" | "new";
  textMetaByKey: Record<string, TextBlockDiffMeta>;
  codeMetaByKey: Record<string, CodeDiffMeta>;
  imageMetaByKey: Record<string, ImageDiffMeta>;
  imageUrls: Record<string, string>;
}): ReactNode {
  if (!html?.trim()) return null;
  const hasAncestorLi = (node: unknown): boolean => {
    let current = (node as { parent?: unknown }).parent as
      | { name?: string; parent?: unknown }
      | undefined;
    while (current) {
      if (current.name === "li") return true;
      current = current.parent as { name?: string; parent?: unknown } | undefined;
    }
    return false;
  };
  const nodeHasCheckbox = (node: unknown): boolean => {
    const maybeNode = node as {
      name?: string;
      attribs?: Record<string, string>;
      children?: unknown[];
    };
    if (
      maybeNode.name === "input" &&
      (maybeNode.attribs?.type ?? "").toLowerCase() === "checkbox"
    ) {
      return true;
    }
    const children = maybeNode.children ?? [];
    return children.some((child) => nodeHasCheckbox(child));
  };
  const imageCounts: Record<string, number> = {};
  const textCounts: Record<"li" | "p", number> = { li: 0, p: 0 };
  let codeCount = 0;
  const options = {
    replace(domNode: unknown) {
      const maybeTag = domNode as {
        name?: string;
        attribs?: Record<string, string>;
      };
      if (maybeTag.name === "li" || maybeTag.name === "p") {
        const kind = maybeTag.name as "li" | "p";
        if (kind === "p" && hasAncestorLi(domNode)) return undefined;
        const occurrence = textCounts[kind];
        textCounts[kind] = occurrence + 1;
        const blockKey = `${kind}::${occurrence}`;
        const textMeta = textMetaByKey[blockKey];
        const isChecklistRow = kind === "li" && nodeHasCheckbox(domNode);
        const blockClass =
          isChecklistRow && textMeta?.status && textMeta.status !== "unchanged"
            ? side === "new"
              ? "bg-emerald-500/12 border border-emerald-400/45 px-1.5 py-0.5 rounded-md"
              : "bg-red-500/12 border border-red-400/45 px-1.5 py-0.5 rounded-md"
            : side === "new" && textMeta?.status === "added"
            ? "bg-emerald-500/10 border border-emerald-400/25 px-1.5 py-0.5 rounded-md my-1"
            : side === "old" && textMeta?.status === "removed"
            ? "bg-red-500/10 border border-red-400/25 px-1.5 py-0.5 rounded-md"
            : textMeta?.status === "updated"
            ? side === "new"
              ? "bg-emerald-500/8 border border-emerald-500/45 px-1.5 py-0.5 rounded-md"
              : "bg-red-500/8 border border-red-500/45 px-1.5 py-0.5 rounded-md"
            : "";

        if (!blockClass) return undefined;

        const nodeWithChildren = domNode as {
          attribs?: Record<string, string>;
          children?: Parameters<typeof domToReact>[0];
        };
        const attribs = nodeWithChildren.attribs ?? {};
        const { class: classAttr, ...safeAttribs } = attribs;
        delete safeAttribs.style;
        const nextClass = `${classAttr ?? ""} ${blockClass}`.trim();
        const children = nodeWithChildren.children ?? [];

        if (kind === "li") {
          return (
            <li {...safeAttribs} className={nextClass}>
              {domToReact(children, options)}
            </li>
          );
        }

        return (
          <p {...safeAttribs} className={nextClass}>
            {domToReact(children, options)}
          </p>
        );
      }

      if (maybeTag.name === "ol" || maybeTag.name === "ul") {
        const nodeWithChildren = domNode as {
          attribs?: Record<string, string>;
          children?: Parameters<typeof domToReact>[0];
        };
        const attribs = nodeWithChildren.attribs ?? {};
        const safeAttribs = { ...attribs };
        delete safeAttribs.class;
        delete safeAttribs.style;
        const listClass =
          maybeTag.name === "ol"
            ? "list-decimal list-outside pl-6"
            : "list-disc list-outside pl-6";
        const children = nodeWithChildren.children ?? [];
        if (maybeTag.name === "ol") {
          return (
            <ol {...safeAttribs} className={listClass}>
              {domToReact(children, options)}
            </ol>
          );
        }
        return (
          <ul {...safeAttribs} className={listClass}>
            {domToReact(children, options)}
          </ul>
        );
      }

      if (maybeTag.name === "input") {
        const attribs = maybeTag.attribs ?? {};
        const type = (attribs.type ?? "").toLowerCase();
        if (type !== "checkbox") return undefined;

        const checkedAttr = attribs.checked;
        const checked =
          checkedAttr !== undefined &&
          checkedAttr !== "false" &&
          checkedAttr !== "0" &&
          checkedAttr !== "";
        const className = `${attribs.class ?? ""} mr-2 align-middle shrink-0 pointer-events-none cursor-default accent-blue-400 disabled:opacity-100`.trim();

        return (
          <input
            type="checkbox"
            checked={checked}
            readOnly
            tabIndex={-1}
            aria-disabled="true"
            className={className}
          />
        );
      }

      if (maybeTag.name === "code") {
        const attribs = maybeTag.attribs ?? {};
        const codeKey = `code::${codeCount++}`;
        const codeMeta = codeMetaByKey[codeKey];
        const codeClass =
          side === "new" && codeMeta?.status === "added"
            ? "bg-emerald-500/25 text-emerald-100 ring-1 ring-emerald-400/50"
            : side === "old" && codeMeta?.status === "removed"
            ? "bg-red-500/25 text-red-100 line-through ring-1 ring-red-400/50"
            : codeMeta?.status === "updated"
            ? side === "new"
              ? "bg-emerald-500/20 text-emerald-100 ring-1 ring-emerald-500/40"
              : "bg-red-500/20 text-red-100 ring-1 ring-red-500/40"
            : "";
        if (!codeClass) return undefined;
        const children = (domNode as { children?: Parameters<typeof domToReact>[0] }).children ?? [];
        return (
          <code className={`${attribs.class ?? ""} ${codeClass} rounded px-1 py-0.5`.trim()}>
            {domToReact(children, options)}
          </code>
        );
      }

      if (maybeTag.name !== "img") return undefined;

      const attribs = maybeTag.attribs ?? {};
      const rawId = attribs["data-image-id"] ?? "";
      const id = normalizeImageId(rawId);
      const occurrence = imageCounts[id] ?? 0;
      imageCounts[id] = occurrence + 1;
      const imageKey = `${id}::${occurrence}`;
      const srcAttr = attribs.src ?? "";
      const fallbackSrc = id ? imageUrls[id] : "";
      const src = srcAttr && !srcAttr.startsWith("image://") ? srcAttr : fallbackSrc;
      const existingClass = attribs.class ?? "";
      const meta = imageMetaByKey[imageKey];
      const isAdded = meta?.status === "added";
      const isRemoved = meta?.status === "removed";
      const isResized = meta?.status === "resized";
      const beforeSize = meta?.before;
      const afterSize = meta?.after;
      const beforeLabel = formatDimensionsLabel(beforeSize);
      const afterLabel = formatDimensionsLabel(afterSize);
      const diffClass =
        side === "new" && isAdded
          ? " ring-2 ring-emerald-500/70"
          : side === "old" && isRemoved
          ? " ring-2 ring-red-500/70 opacity-60"
          : isResized
          ? side === "new"
            ? " ring-2 ring-blue-500/70"
            : " ring-2 ring-amber-500/70"
          : "";

      const widthAttr = attribs.width;
      const heightAttr = attribs.height;
      const widthFromAttr = widthAttr ? Number(widthAttr) : undefined;
      const heightFromAttr = heightAttr ? Number(heightAttr) : undefined;
      const width =
        Number.isFinite(widthFromAttr) && widthFromAttr && widthFromAttr > 0
          ? widthFromAttr
          : 300;
      const height =
        Number.isFinite(heightFromAttr) && heightFromAttr && heightFromAttr > 0
          ? heightFromAttr
          : 200;

      return (
        <span className="relative inline-block align-top mr-2 mb-2">
          <img
            {...attribs}
            src={src}
            width={width}
            height={height}
            style={
              {
                width: `${width}px`,
                minWidth: `${width}px`,
                maxWidth: `${width}px`,
                height: `${height}px`,
                minHeight: `${height}px`,
                maxHeight: `${height}px`,
              } as React.CSSProperties
            }
            className={`${existingClass} rounded-md${diffClass}`.trim()}
            loading="lazy"
            decoding="async"
          />
          {isResized && beforeLabel && afterLabel && (
            <span
              className={`absolute left-1 top-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                side === "new"
                  ? "bg-blue-500/85 text-white"
                  : "bg-amber-500/85 text-black"
              }`}
            >
              {`${beforeLabel} → ${afterLabel}`}
            </span>
          )}
          {isAdded && side === "new" && (
            <span className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-500/85 text-white">
              New
            </span>
          )}
          {isRemoved && side === "old" && (
            <span className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-red-500/85 text-white">
              Removed
            </span>
          )}
        </span>
      );
    },
  };

  return parse(html, options);
}

/** Inline word-level diff: removed (strikethrough red), added (green), unchanged (default). */
function InlineWordDiff({
  oldText,
  newText,
  className = "",
}: {
  oldText: string;
  newText: string;
  className?: string;
}) {
  const oldClean = oldText.trim();
  const newClean = newText.trim();
  if (oldClean === newClean) {
    return <span className={className}>{newClean || "—"}</span>;
  }
  const chunks = diffWords(oldClean, newClean);
  return (
    <span className={className}>
      {chunks.map((chunk, i) => {
        if (chunk.added) {
          return (
            <span key={i} className="bg-emerald-500/20 text-emerald-300 rounded px-0.5">
              {chunk.value}
            </span>
          );
        }
        if (chunk.removed) {
          return (
            <span key={i} className="bg-red-500/20 text-red-400 line-through rounded px-0.5">
              {chunk.value}
            </span>
          );
        }
        return <span key={i}>{chunk.value}</span>;
      })}
    </span>
  );
}

/** One-sided word diff for split view — only highlights the relevant side. */
function SplitSideWordDiff({
  oldText,
  newText,
  side,
  className = "",
}: {
  oldText: string;
  newText: string;
  side: "old" | "new";
  className?: string;
}) {
  const oldClean = (oldText ?? "").trim();
  const newClean = (newText ?? "").trim();
  if (oldClean === newClean) {
    return <span className={className}>{side === "old" ? oldClean : newClean}</span>;
  }
  const chunks = diffWords(oldClean, newClean);
  return (
    <span className={className}>
      {chunks.map((chunk, i) => {
        if (chunk.added) {
          if (side === "new")
            return (
              <span key={i} className="bg-emerald-500/20 text-emerald-300 rounded px-0.5">
                {chunk.value}
              </span>
            );
          return null;
        }
        if (chunk.removed) {
          if (side === "old")
            return (
              <span key={i} className="bg-red-500/20 text-red-400 line-through rounded px-0.5">
                {chunk.value}
              </span>
            );
          return null;
        }
        return <span key={i}>{chunk.value}</span>;
      })}
    </span>
  );
}

// ─── Step row type ────────────────────────────────────────────────────────────

type StepRow = {
  index: number;
  baseStep: Step | null;
  compareStep: Step | null;
  diff: DiffResult<Step> | null;
  isChanged: boolean;
};

// ─── StepCard ─────────────────────────────────────────────────────────────────

type StepCardProps = {
  row: StepRow;
  viewMode: "split" | "unified";
  isActive: boolean;
  onSelect: () => void;
  imageUrls: Record<string, string>;
  baseLabel: string;
  compareLabel: string;
  cardRef?: (el: HTMLDivElement | null) => void;
};

function StepCard({
  row,
  viewMode,
  isActive,
  onSelect,
  imageUrls,
  baseLabel,
  compareLabel,
  cardRef,
}: StepCardProps) {
  const { index, baseStep, compareStep, diff, isChanged } = row;
  const [open, setOpen] = useState(isChanged);

  const stepTitle = compareStep?.title ?? baseStep?.title ?? `Step ${index + 1}`;

  const handleHeaderClick = () => {
    setOpen((o) => !o);
    onSelect();
  };

  return (
    <div
      ref={cardRef}
      className={`rounded-xl border transition-all ${
        isActive
          ? "border-blue-500/40 shadow-[0_0_0_3px_rgba(59,130,246,0.1)]"
          : isChanged
          ? "border-slate-700/60"
          : "border-slate-800/50"
      } bg-slate-900/40`}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer select-none rounded-t-xl ${
          open ? "border-b border-slate-700/30" : "rounded-b-xl"
        } ${isChanged ? "bg-slate-800/40" : "bg-slate-900/20"}`}
        onClick={handleHeaderClick}
      >
        <span
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
            isChanged ? "bg-blue-500/80" : "bg-slate-700/60"
          }`}
        >
          {index + 1}
        </span>
        <span
          className={`font-medium text-sm flex-1 min-w-0 truncate ${
            isChanged ? "text-slate-200" : "text-slate-500"
          }`}
        >
          {stepTitle}
        </span>
        {isChanged && !baseStep && (
          <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 flex-shrink-0">
            Added
          </span>
        )}
        {isChanged && !compareStep && (
          <span className="text-[10px] font-semibold text-red-400 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5 flex-shrink-0">
            Removed
          </span>
        )}
        {!isChanged && (
          <span className="text-[10px] font-semibold text-amber-400 bg-amber-600/20 border border-amber-600/40 rounded-full px-2 py-0.5 flex-shrink-0">
            No changes
          </span>
        )}
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        )}
      </div>

      {open && (
        viewMode === "split"
          ? <SplitBody
              baseStep={baseStep}
              compareStep={compareStep}
              isChanged={isChanged}
              imageUrls={imageUrls}
              baseLabel={baseLabel}
              compareLabel={compareLabel}
            />
          : <UnifiedBody
              baseStep={baseStep}
              compareStep={compareStep}
              diff={diff}
              imageUrls={imageUrls}
            />
      )}
    </div>
  );
}

// ─── Split view body ──────────────────────────────────────────────────────────

function SplitBody({
  baseStep,
  compareStep,
  isChanged,
  imageUrls,
  baseLabel,
  compareLabel,
}: {
  baseStep: Step | null;
  compareStep: Step | null;
  isChanged: boolean;
  imageUrls: Record<string, string>;
  baseLabel: string;
  compareLabel: string;
}) {
  // Added step
  if (!baseStep && compareStep) {
    const compareDetailsHtml = compareStep.details ?? "";
    const imageMeta = buildImageDiffMeta("", compareDetailsHtml);
    const textMeta = buildTextBlockDiffMeta("", compareDetailsHtml);
    const codeMeta = buildCodeDiffMeta("", compareDetailsHtml);
    return (
      <div className="grid grid-cols-2 divide-x divide-slate-700/30 rounded-b-xl overflow-hidden">
        <div className="px-4 py-6 flex items-center justify-center min-h-[60px] bg-slate-900/20">
          <span className="text-slate-700 text-xs italic">— New step —</span>
        </div>
        <div className="px-4 py-4 space-y-2 bg-emerald-500/5">
          {compareStep.title && (
            <p className="text-sm font-medium text-emerald-300">{compareStep.title}</p>
          )}
          {compareStep.details && (
            <div className="text-sm text-emerald-300/70 leading-relaxed [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_li]:leading-relaxed [&_li::marker]:text-slate-400">
              {renderStepHtmlWithImageDiff({
                html: compareDetailsHtml,
                side: "new",
                textMetaByKey: textMeta.compareMetaByKey,
                codeMetaByKey: codeMeta.compareMetaByKey,
                imageMetaByKey: imageMeta.compareMetaByKey,
                imageUrls,
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Removed step
  if (baseStep && !compareStep) {
    const baseDetailsHtml = baseStep.details ?? "";
    const imageMeta = buildImageDiffMeta(baseDetailsHtml, "");
    const textMeta = buildTextBlockDiffMeta(baseDetailsHtml, "");
    const codeMeta = buildCodeDiffMeta(baseDetailsHtml, "");
    return (
      <div className="grid grid-cols-2 divide-x divide-slate-700/30 rounded-b-xl overflow-hidden">
        <div className="px-4 py-4 space-y-2 bg-red-500/5">
          {baseStep.title && (
            <p className="text-sm font-medium text-red-300 line-through">{baseStep.title}</p>
          )}
          {baseStep.details && (
            <div className="text-sm text-red-300/70 leading-relaxed [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ol]:my-2 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_ul]:my-2 [&_li]:my-1 [&_li]:leading-relaxed [&_li::marker]:text-slate-400">
              {renderStepHtmlWithImageDiff({
                html: baseDetailsHtml,
                side: "old",
                textMetaByKey: textMeta.baseMetaByKey,
                codeMetaByKey: codeMeta.baseMetaByKey,
                imageMetaByKey: imageMeta.baseMetaByKey,
                imageUrls,
              })}
            </div>
          )}
        </div>
        <div className="px-4 py-6 flex items-center justify-center min-h-[60px] bg-slate-900/20">
          <span className="text-slate-700 text-xs italic">— Step removed —</span>
        </div>
      </div>
    );
  }

  if (!baseStep || !compareStep) return null;

  if (!isChanged) {
    const step = compareStep ?? baseStep;
    if (!step) return null;
    const detailsHtml = step.details ?? "";
    const imageMeta = buildImageDiffMeta(detailsHtml, detailsHtml);
    const textMeta = buildTextBlockDiffMeta(detailsHtml, detailsHtml);
    const codeMeta = buildCodeDiffMeta(detailsHtml, detailsHtml);
    return (
      <div className="rounded-b-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-700/30 bg-slate-900/30">
          <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
            No changes between {baseLabel} and {compareLabel}
          </span>
        </div>
        <div className="px-4 py-3 space-y-3">
          {step.title && (
            <div>
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                Title
              </p>
              <p className="text-sm text-slate-200">{step.title}</p>
            </div>
          )}
          {detailsHtml && (
            <div>
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                Details
              </p>
              <div className="text-sm text-slate-300 leading-relaxed [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ol]:my-2 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_ul]:my-2 [&_li]:my-1 [&_li]:leading-relaxed [&_li::marker]:text-slate-400">
                {renderStepHtmlWithImageDiff({
                  html: detailsHtml,
                  side: "new",
                  textMetaByKey: textMeta.compareMetaByKey,
                  codeMetaByKey: codeMeta.compareMetaByKey,
                  imageMetaByKey: imageMeta.compareMetaByKey,
                  imageUrls,
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const titleOld = baseStep.title ?? "";
  const titleNew = compareStep.title ?? "";
  const detailsOldHtml = baseStep.details ?? "";
  const detailsNewHtml = compareStep.details ?? "";
  const detailsOldText = htmlToDiffText(detailsOldHtml);
  const detailsNewText = htmlToDiffText(detailsNewHtml);
  const imageMeta = buildImageDiffMeta(detailsOldHtml, detailsNewHtml);
  const textMeta = buildTextBlockDiffMeta(detailsOldHtml, detailsNewHtml);
  const codeMeta = buildCodeDiffMeta(detailsOldHtml, detailsNewHtml);
  const partChanges = buildPartChanges(baseStep, compareStep).filter(
    (row) => row.status !== "unchanged",
  );

  const hasAnyContent = titleOld || titleNew || detailsOldText || detailsNewText;

  if (!hasAnyContent) {
    return (
      <div className="px-4 py-4 text-slate-500 text-sm italic">No content changes detected.</div>
    );
  }

  return (
    <div className="rounded-b-xl overflow-hidden">
      {/* Column headers */}
      <div className="grid grid-cols-2 divide-x divide-slate-700/30 border-b border-slate-700/30">
        <div className="px-4 py-2 bg-slate-900/40">
          <span className="text-[10px] font-bold tracking-wider text-amber-500/60 uppercase">
            ← {baseLabel}
          </span>
        </div>
        <div className="px-4 py-2 bg-slate-900/40">
          <span className="text-[10px] font-bold tracking-wider text-emerald-500/60 uppercase">
            {compareLabel} →
          </span>
        </div>
      </div>

      {/* Title */}
      {(titleOld || titleNew) && (
        <div className="border-b border-slate-800/60">
          <div className="px-4 pt-3 pb-1">
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              Title
            </span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-700/30">
            <div className="px-4 pb-3 text-sm text-slate-300 leading-relaxed">
              <SplitSideWordDiff oldText={titleOld} newText={titleNew} side="old" />
            </div>
            <div className="px-4 pb-3 text-sm text-slate-300 leading-relaxed">
              <SplitSideWordDiff oldText={titleOld} newText={titleNew} side="new" />
            </div>
          </div>
        </div>
      )}

      {/* Details */}
      {(detailsOldText || detailsNewText) && (
        <div className="border-b border-slate-800/60">
          <div className="px-4 pt-3 pb-1">
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              Details
            </span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-700/30">
            <div className="px-4 pb-3 text-sm text-slate-300 leading-relaxed [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ol]:my-2 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_ul]:my-2 [&_li]:my-1 [&_li]:leading-relaxed [&_li::marker]:text-slate-400">
              {renderStepHtmlWithImageDiff({
                html: detailsOldHtml,
                side: "old",
                textMetaByKey: textMeta.baseMetaByKey,
                codeMetaByKey: codeMeta.baseMetaByKey,
                imageMetaByKey: imageMeta.baseMetaByKey,
                imageUrls,
              })}
            </div>
            <div className="px-4 pb-3 text-sm text-slate-300 leading-relaxed [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ol]:my-2 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_ul]:my-2 [&_li]:my-1 [&_li]:leading-relaxed [&_li::marker]:text-slate-400">
              {renderStepHtmlWithImageDiff({
                html: detailsNewHtml,
                side: "new",
                textMetaByKey: textMeta.compareMetaByKey,
                codeMetaByKey: codeMeta.compareMetaByKey,
                imageMetaByKey: imageMeta.compareMetaByKey,
                imageUrls,
              })}
            </div>
          </div>
        </div>
      )}

      {partChanges.length > 0 && (
        <div className="border-b border-slate-800/60">
          <div className="px-4 pt-3 pb-1">
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              Parts changes
            </span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-700/30 pb-3">
            <div className="px-4 space-y-1">
              {partChanges.map((row) => (
                <div key={`parts-old-${row.index}`} className="rounded-md bg-slate-900/30 border border-slate-800/60 p-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Part name</p>
                  <p className="text-xs text-slate-300">
                    {row.status === "added" ? (
                      <span className="text-slate-600 italic">—</span>
                    ) : row.status === "removed" ? (
                      <span className="text-red-300 line-through">{partName(row.basePart)}</span>
                    ) : (
                      <SplitSideWordDiff
                        oldText={partName(row.basePart)}
                        newText={partName(row.comparePart)}
                        side="old"
                      />
                    )}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">Units</p>
                  <p className="text-xs text-slate-300">
                    {row.status === "added" ? (
                      <span className="text-slate-600 italic">—</span>
                    ) : row.status === "removed" ? (
                      <span className="text-red-300 line-through">{partUnits(row.basePart)}</span>
                    ) : (
                      <SplitSideWordDiff
                        oldText={partUnits(row.basePart)}
                        newText={partUnits(row.comparePart)}
                        side="old"
                      />
                    )}
                  </p>
                </div>
              ))}
            </div>
            <div className="px-4 space-y-1">
              {partChanges.map((row) => (
                <div key={`parts-new-${row.index}`} className="rounded-md bg-slate-900/30 border border-slate-800/60 p-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Part name</p>
                  <p className="text-xs text-slate-300">
                    {row.status === "removed" ? (
                      <span className="text-slate-600 italic">—</span>
                    ) : row.status === "added" ? (
                      <span className="text-emerald-300">{partName(row.comparePart)}</span>
                    ) : (
                      <SplitSideWordDiff
                        oldText={partName(row.basePart)}
                        newText={partName(row.comparePart)}
                        side="new"
                      />
                    )}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">Units</p>
                  <p className="text-xs text-slate-300">
                    {row.status === "removed" ? (
                      <span className="text-slate-600 italic">—</span>
                    ) : row.status === "added" ? (
                      <span className="text-emerald-300">{partUnits(row.comparePart)}</span>
                    ) : (
                      <SplitSideWordDiff
                        oldText={partUnits(row.basePart)}
                        newText={partUnits(row.comparePart)}
                        side="new"
                      />
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Unified view body ────────────────────────────────────────────────────────

function UnifiedBody({
  baseStep,
  compareStep,
  diff,
  imageUrls,
}: {
  baseStep: Step | null;
  compareStep: Step | null;
  diff: DiffResult<Step> | null;
  imageUrls: Record<string, string>;
}) {
  if (!baseStep && compareStep) {
    return (
      <div className="px-4 py-3">
        <p className="text-emerald-400/90 text-sm">
          <span className="font-medium">Step added.</span> Title: {compareStep.title}
        </p>
      </div>
    );
  }
  if (baseStep && !compareStep) {
    return (
      <div className="px-4 py-3">
        <p className="text-red-400/90 text-sm">
          <span className="font-medium">Step removed.</span> Was: {baseStep.title}
        </p>
      </div>
    );
  }
  if (!baseStep || !compareStep || !diff) {
    const step = compareStep ?? baseStep;
    if (!step) return null;
    const imageMeta = buildImageDiffMeta(baseStep?.details ?? "", compareStep?.details ?? "");
    const textMeta = buildTextBlockDiffMeta(baseStep?.details ?? "", compareStep?.details ?? "");
    const codeMeta = buildCodeDiffMeta(baseStep?.details ?? "", compareStep?.details ?? "");
    return (
      <div className="px-4 py-3 space-y-2">
        {step.title && <p className="text-sm text-slate-400">{step.title}</p>}
        {step.details && (
          <div className="text-sm text-slate-500 leading-relaxed [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ol]:my-2 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_ul]:my-2 [&_li]:my-1 [&_li]:leading-relaxed [&_li::marker]:text-slate-400">
            {renderStepHtmlWithImageDiff({
              html: step.details,
              side: compareStep ? "new" : "old",
              textMetaByKey: compareStep ? textMeta.compareMetaByKey : textMeta.baseMetaByKey,
              codeMetaByKey: compareStep ? codeMeta.compareMetaByKey : codeMeta.baseMetaByKey,
              imageMetaByKey: compareStep ? imageMeta.compareMetaByKey : imageMeta.baseMetaByKey,
              imageUrls,
            })}
          </div>
        )}
      </div>
    );
  }

  const titleOld = baseStep?.title ?? "";
  const titleNew =
    typeof diff.title === "object" ? String(diff.title?.value ?? "") : "";
  const detailsOld = baseStep?.details ?? "";
  const detailsNew = compareStep?.details ?? "";
  const titleChanged = diff.title?.highlight !== "none";
  const detailsChanged =
    normalizeHtmlIgnoringClass(detailsOld) !== normalizeHtmlIgnoringClass(detailsNew);
  const imageMeta = buildImageDiffMeta(detailsOld, detailsNew);
  const textMeta = buildTextBlockDiffMeta(detailsOld, detailsNew);
  const codeMeta = buildCodeDiffMeta(detailsOld, detailsNew);
  const partChanges = buildPartChanges(baseStep, compareStep).filter(
    (row) => row.status !== "unchanged",
  );

  const hasAnyChange =
    titleChanged ||
    detailsChanged ||
    partChanges.length > 0;

  if (!hasAnyChange) {
    return (
      <div className="px-4 py-3">
        <p className="text-slate-500 text-sm italic">No content changes.</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 space-y-4">
      {titleChanged && (
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
            Title
          </p>
          <p className="text-sm leading-relaxed">
            <InlineWordDiff oldText={titleOld} newText={titleNew} />
          </p>
        </div>
      )}
      {detailsChanged && (
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
            Details
          </p>
          <div className="text-sm leading-relaxed [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ol]:my-2 [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_ul]:my-2 [&_li]:my-1 [&_li]:leading-relaxed [&_li::marker]:text-slate-400">
            {renderStepHtmlWithImageDiff({
              html: detailsNew,
              side: "new",
              textMetaByKey: textMeta.compareMetaByKey,
              codeMetaByKey: codeMeta.compareMetaByKey,
              imageMetaByKey: imageMeta.compareMetaByKey,
              imageUrls,
            })}
          </div>
        </div>
      )}
      {partChanges.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
            Parts changes
          </p>
          <div className="space-y-1.5">
            {partChanges.map((row) => (
              <div key={`parts-unified-${row.index}`} className="rounded-md bg-slate-900/30 border border-slate-800/60 p-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Part name</p>
                <p className="text-xs leading-relaxed">
                  {row.status === "added" ? (
                    <span className="text-emerald-300">+ {partName(row.comparePart)}</span>
                  ) : row.status === "removed" ? (
                    <span className="text-red-300 line-through">- {partName(row.basePart)}</span>
                  ) : (
                    <>
                      <span className="text-red-300 line-through">{partName(row.basePart)}</span>
                      <span className="text-slate-500 mx-1">→</span>
                      <span className="text-emerald-300">{partName(row.comparePart)}</span>
                    </>
                  )}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">Units</p>
                <p className="text-xs leading-relaxed">
                  {row.status === "added" ? (
                    <span className="text-emerald-300">+ {partUnits(row.comparePart)}</span>
                  ) : row.status === "removed" ? (
                    <span className="text-red-300 line-through">- {partUnits(row.basePart)}</span>
                  ) : (
                    <>
                      <span className="text-red-300 line-through">{partUnits(row.basePart)}</span>
                      <span className="text-slate-500 mx-1">→</span>
                      <span className="text-emerald-300">{partUnits(row.comparePart)}</span>
                    </>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const SOPVersions: FC = () => {
  const { sopId } = useParams<{ sopId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isSuperAdmin } = useSuperAdmin();
  const backTo =
    (location.state as { from?: string } | null)?.from ??
    (sopId ? `/sop/${sopId}` : "/sop-dashboard");
  const versionList = useSopStore((s) =>
    sopId ? s.versionListsBySopId[sopId] ?? EMPTY_VERSION_LIST : EMPTY_VERSION_LIST,
  );

  const [sop, setSop] = useState<SOP | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectingFrom, setSelectingFrom] = useState(false);
  const [selectingTo, setSelectingTo] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [baseVersion, setBaseVersion] = useState<SOPVersion | null>(null);
  const [compareVersion, setCompareVersion] = useState<SOPVersion | null>(null);
  const [viewMode, setViewMode] = useState<"split" | "unified">("split");
  const [stepFilter, setStepFilter] = useState<"changes" | "all">("changes");
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);

  const mainScrollRef = useRef<HTMLDivElement>(null);
  const stepCardRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!sopId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);
    const run = async () => {
      try {
        const { fetchSOPById, getSopById, getVersionList } = useSopStore.getState();
        await fetchSOPById(sopId);
        if (cancelled) return;
        const loaded = getSopById(sopId);
        setSop(loaded ?? null);
        const list = getVersionList(sopId);
        const sorted = [...list].sort((a, b) => a.versionNumber - b.versionNumber);
        if (cancelled) return;
        if (sorted.length >= 2) {
          const baseId = sorted[sorted.length - 2].versionId;
          const compareId = sorted[sorted.length - 1].versionId;
          const [base, compare] = await Promise.all([
            getVersion(sopId, baseId),
            getVersion(sopId, compareId),
          ]);
          if (cancelled) return;
          setBaseVersion(base);
          setCompareVersion(compare);
        } else if (sorted.length === 1) {
          const full = await getVersion(sopId, sorted[0].versionId);
          if (cancelled) return;
          setBaseVersion(null);
          setCompareVersion(full);
        } else {
          setBaseVersion(null);
          setCompareVersion(null);
        }
      } catch (err) {
        if (!cancelled) {
          setSop(null);
          setBaseVersion(null);
          setCompareVersion(null);
          const msg = isPermissionDeniedError(err)
            ? PERMISSION_DENIED_MESSAGE
            : (() => {
                const data =
                  err &&
                  typeof err === "object" &&
                  "response" in err &&
                  (err as { response?: { data?: unknown } }).response?.data;
                if (data && typeof data === "object") {
                  const d = data as {
                    error?: string | { message?: string };
                    message?: string;
                  };
                  return (
                    (typeof d.error === "string"
                      ? d.error
                      : d.error?.message ?? d.message ?? null) ?? "Failed to load SOP versions"
                  );
                }
                return err instanceof Error ? err.message : "Failed to load SOP versions";
              })();
          setErrorMessage(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sopId]);

  const sortedVersions = useMemo(() => {
    if (!versionList.length) return [];
    return [...versionList].sort((a, b) => a.versionNumber - b.versionNumber);
  }, [versionList]);

  const { draftVersionId, latestReleaseVersionId } = useMemo(() => {
    const done = sortedVersions.filter((v) => v.status === "done");
    const draft = sortedVersions.find((v) => v.status === "draft");
    const latestRelease =
      done.length > 0
        ? done.reduce((best, v) => (v.versionNumber > (best?.versionNumber ?? -1) ? v : best))
        : null;
    return {
      draftVersionId: draft?.versionId ?? null,
      latestReleaseVersionId: latestRelease?.versionId ?? null,
    };
  }, [sortedVersions]);

  const maxSteps = useMemo(() => {
    const baseLen = baseVersion?.steps?.length ?? 0;
    const compareLen = compareVersion?.steps?.length ?? 0;
    return Math.max(baseLen, compareLen, 0);
  }, [baseVersion?.steps?.length, compareVersion?.steps?.length]);

  const stepsWithChanges = useMemo(() => {
    const out: {
      index: number;
      baseStep: Step | null;
      compareStep: Step | null;
      diff: DiffResult<Step>;
    }[] = [];
    for (let i = 0; i < maxSteps; i++) {
      const baseStep = baseVersion?.steps?.[i] ?? null;
      const compareStep = compareVersion?.steps?.[i] ?? null;
      const emptyStep: Step = {
        title: "",
        details: "",
        parts: [],
        tools: [],
        checklist: [],
        imageReferences: [],
      };
      const oldStep = baseStep ?? emptyStep;
      const newStep = compareStep ?? emptyStep;
      const diff = compareObjects(oldStep, newStep);
      const detailsChangedIgnoringClass =
        normalizeHtmlIgnoringClass(baseStep?.details ?? "") !==
        normalizeHtmlIgnoringClass(compareStep?.details ?? "");
      const hasChange =
        (diff.title?.highlight && diff.title.highlight !== "none") ||
        detailsChangedIgnoringClass ||
        diff.parts?.some(
          (p: { name?: { highlight?: string } }) => p?.name?.highlight !== "none",
        ) ||
        diff.tools?.some(
          (t: { name?: { highlight?: string } }) => t?.name?.highlight !== "none",
        ) ||
        diff.checklist?.some(
          (c: { description?: { highlight?: string } }) => c?.description?.highlight !== "none",
        ) ||
        (baseStep &&
          compareStep &&
          ((baseStep.parts?.length ?? 0) > (compareStep.parts?.length ?? 0) ||
            (baseStep.tools?.length ?? 0) > (compareStep.tools?.length ?? 0))) ||
        (baseStep &&
          compareStep &&
          (baseStep.imageReferences?.length ?? 0) > (compareStep.imageReferences?.length ?? 0)) ||
        !baseStep ||
        !compareStep;
      if (hasChange) out.push({ index: i, baseStep, compareStep, diff });
    }
    return out;
  }, [baseVersion?.steps, compareVersion?.steps, maxSteps]);

  const allStepRows = useMemo<StepRow[]>(() => {
    return Array.from({ length: maxSteps }, (_, i) => {
      const changedEntry = stepsWithChanges.find((s) => s.index === i);
      return {
        index: i,
        baseStep: baseVersion?.steps?.[i] ?? null,
        compareStep: compareVersion?.steps?.[i] ?? null,
        diff: changedEntry?.diff ?? null,
        isChanged: !!changedEntry,
      };
    });
  }, [baseVersion?.steps, compareVersion?.steps, maxSteps, stepsWithChanges]);

  const visibleStepRows = useMemo(
    () => (stepFilter === "changes" ? allStepRows.filter((r) => r.isChanged) : allStepRows),
    [allStepRows, stepFilter],
  );

  const diffImageIds = useMemo(() => {
    const ids = new Set<string>();
    allStepRows.forEach(({ baseStep, compareStep }) => {
      extractImageIdsFromHtml(baseStep?.details ?? "").forEach((id) =>
        ids.add(normalizeImageId(id)),
      );
      extractImageIdsFromHtml(compareStep?.details ?? "").forEach((id) =>
        ids.add(normalizeImageId(id)),
      );
    });
    return Array.from(ids);
  }, [allStepRows]);

  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (diffImageIds.length === 0) return;
    getImageUrls(diffImageIds)
      .then((urls) => setImageUrls((prev) => ({ ...prev, ...urls })))
      .catch(() => {});
  }, [diffImageIds]);

  const canCompare =
    baseVersion &&
    compareVersion &&
    baseVersion.versionId !== compareVersion.versionId;

  const formatDate = (iso: string) => {
    const d = new Date(iso.split(".")[0] + "Z");
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const versionLabelFromItem = (item: VersionListItem, i: number) =>
    i === 0 ? "Original" : `Version ${item.versionNumber}`;

  const versionOptions = useMemo<VersionOption[]>(
    () =>
      sortedVersions.map((item, i) => {
        const badges: VersionOption["badges"] = [];
        if (item.versionId === latestReleaseVersionId) badges.push("latest");
        if (item.versionId === draftVersionId) badges.push("draft");
        if (i === 0) badges.push("original");
        return {
          versionId: item.versionId,
          label: versionLabelFromItem(item, i),
          dateText: item.editedAt ? formatDate(item.editedAt) : null,
          badges,
        };
      }),
    [sortedVersions, latestReleaseVersionId, draftVersionId],
  );

  const baseLabel = useMemo(() => {
    if (!baseVersion) return "Base";
    const idx = sortedVersions.findIndex((v) => v.versionId === baseVersion.versionId);
    return idx === 0 ? "Original" : `Version ${baseVersion.versionNum ?? idx + 1}`;
  }, [baseVersion, sortedVersions]);

  const compareLabel = useMemo(() => {
    if (!compareVersion) return "Compare";
    const idx = sortedVersions.findIndex((v) => v.versionId === compareVersion.versionId);
    return idx === 0 ? "Original" : `Version ${compareVersion.versionNum ?? idx + 1}`;
  }, [compareVersion, sortedVersions]);

  const handleSetBase = useCallback(
    async (versionId: string) => {
      if (!sopId || !versionId) return;
      setSelectingFrom(true);
      const full = await getVersion(sopId, versionId);
      setBaseVersion(full);
      if (compareVersion?.versionId === versionId) {
        setCompareVersion(baseVersion);
      }
      setSelectingFrom(false);
    },
    [sopId, compareVersion?.versionId, baseVersion],
  );

  const handleSetHead = useCallback(
    async (versionId: string) => {
      if (!sopId || !versionId) return;
      setSelectingTo(true);
      const full = await getVersion(sopId, versionId);
      setCompareVersion(full);
      if (baseVersion?.versionId === versionId) {
        setBaseVersion(compareVersion);
      }
      setSelectingTo(false);
    },
    [sopId, baseVersion?.versionId, compareVersion],
  );

  const handleSwapVersions = useCallback(() => {
    setBaseVersion(compareVersion);
    setCompareVersion(baseVersion);
  }, [baseVersion, compareVersion]);

  const scrollToStep = useCallback((index: number) => {
    setActiveStepIndex(index);
    const el = stepCardRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
        <Header />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <Loader className="w-10 h-10 animate-spin text-blue-400" />
            <p className="text-slate-400">Loading versions...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!sopId || !sop) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
        <Header />
        <main className="pt-20 px-4 max-w-4xl mx-auto">
          <p className="text-slate-400">{errorMessage ?? "SOP not found."}</p>
          <Link
            to="/sop-dashboard"
            className="mt-4 inline-flex items-center gap-2 text-blue-400 hover:text-blue-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SOPs
          </Link>
        </main>
      </div>
    );
  }

  const displayTitle = sop.getDisplayTitle();

  if (
    sop.processingStatus === ProcessingStatusEnum.PENDING_VERIFICATION &&
    !isSuperAdmin
  ) {
    return (
      <SopAccessDenied message="This SOP is still being prepared. Please try again later." />
    );
  }

  // ── Main layout ────────────────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />

      <div className="pt-16 flex flex-col flex-1 overflow-hidden">

        {/* Subheader */}
        <div className="px-5 py-3 border-b border-slate-700/50 bg-slate-900/60 flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="p-1.5 rounded-lg bg-slate-800/50 border border-slate-600/50 hover:border-blue-500/50 hover:bg-slate-700/50 transition-colors flex-shrink-0"
            aria-label="Back to SOP"
          >
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </button>
          <div className="flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-blue-400" />
            <h1 className="text-base font-semibold text-white">Compare versions</h1>
          </div>
          <span className="text-slate-400 text-xs px-2.5 py-1 rounded-full border border-slate-700/50 bg-slate-800/50 truncate max-w-[260px]">
            {displayTitle}
          </span>
          <div className="flex-1" />
          {canCompare && (
            <button
              type="button"
              onClick={() => navigate(`/sop/${sopId}/steps`)}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 hover:border-blue-400/60 transition-colors"
            >
              Open in editor →
            </button>
          )}
        </div>

        {/* Version selector strip */}
        {sortedVersions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-slate-400 text-sm">
              No versions available. Open this SOP to create versions.
            </p>
          </div>
        ) : sortedVersions.length === 1 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <p className="text-slate-300 text-sm">This SOP has only one version.</p>
              <p className="text-slate-400 text-sm">
                Edit and save the SOP to create a new version, then return here.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => navigate(`/sop/${sopId}`)}
                  className="px-4 py-2 rounded-lg bg-slate-700/50 border border-slate-600/50 hover:border-blue-500/50 text-sm font-medium text-white transition-colors"
                >
                  Open title page
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/sop/${sopId}/steps`)}
                  className="px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-500/50 hover:bg-blue-500/30 text-sm font-medium text-blue-300 transition-colors"
                >
                  Open steps
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Version picker + legend */}
            <div className="px-5 py-3 border-b border-slate-700/40 bg-slate-800/20 flex items-center gap-3 flex-shrink-0 relative z-[130]">
              <div className="w-64 flex-shrink-0">
                <label className="block text-[10px] font-semibold text-amber-400 mb-1 uppercase tracking-wider">
                  From
                </label>
                <VersionSearchSelect
                  tone="amber"
                  value={baseVersion?.versionId ?? ""}
                  options={versionOptions}
                  onSelect={handleSetBase}
                  disabled={selectingFrom}
                  loading={selectingFrom}
                />
              </div>

              <button
                type="button"
                onClick={handleSwapVersions}
                disabled={!baseVersion || !compareVersion}
                title="Swap versions"
                className="mt-4 flex-shrink-0 p-2 rounded-lg border border-slate-600/50 bg-slate-800/40 hover:border-slate-500 hover:bg-slate-700/50 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>

              <div className="w-64 flex-shrink-0">
                <label className="block text-[10px] font-semibold text-emerald-400 mb-1 uppercase tracking-wider">
                  To
                </label>
                <VersionSearchSelect
                  tone="emerald"
                  value={compareVersion?.versionId ?? ""}
                  options={versionOptions}
                  onSelect={handleSetHead}
                  disabled={selectingTo}
                  loading={selectingTo}
                />
              </div>

              <div className="flex-1" />

              {canCompare && (
                <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-shrink-0 flex-wrap justify-end">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-900/60 border border-emerald-700/60 inline-block" />
                    Added
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-red-900/60 border border-red-700/60 inline-block" />
                    Removed
                  </span>
                  {(compareVersion!.editorEmail ||
                    compareVersion!.editorName ||
                    compareVersion!.editor) && (
                    <>
                      <span className="text-slate-700">·</span>
                      <span>
                        by{" "}
                        <span className="text-slate-400">
                          {compareVersion!.editorEmail ??
                            compareVersion!.editorName ??
                            compareVersion!.editor}
                        </span>
                        {compareVersion!.editedAt
                          ? ` on ${formatDate(compareVersion!.editedAt)}`
                          : ""}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Body */}
            {canCompare ? (
              <div className="flex flex-1 overflow-hidden">

                {/* Sidebar */}
                <aside className="w-[300px] flex-shrink-0 bg-gradient-to-b from-slate-950/95 to-[#020a22] border-r border-slate-700/50 flex flex-col overflow-hidden">
                  <div className="px-5 pt-6 pb-3 border-b border-slate-700/40 flex-shrink-0">
                    <p className="text-[22px] leading-none font-semibold text-slate-100 mb-4">
                      Compare versions
                    </p>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.14em] mb-2">
                      Steps
                    </p>
                    <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-900/75 p-1 border border-slate-700/70">
                      {(["changes", "all"] as const).map((filter) => (
                        <button
                          key={filter}
                          type="button"
                          onClick={() => setStepFilter(filter)}
                          className={`py-1.5 rounded-md text-[11px] font-semibold transition-all ${
                            stepFilter === filter
                              ? "bg-blue-500/25 text-blue-100 border border-blue-400/40 shadow-[0_0_12px_rgba(59,130,246,0.25)]"
                              : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/70 border border-transparent"
                          }`}
                        >
                          {filter === "changes" ? "Changed" : "All"}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto py-4 px-3 space-y-2">
                    {allStepRows
                      .filter((row) => stepFilter === "all" || row.isChanged)
                      .map((row) => {
                        const isActive = activeStepIndex === row.index;
                        const title =
                          row.compareStep?.title ??
                          row.baseStep?.title ??
                          `Step ${row.index + 1}`;
                        return (
                          <button
                            key={row.index}
                            type="button"
                            onClick={() => scrollToStep(row.index)}
                            className={`w-full text-left rounded-xl px-3 py-3 transition-all ${
                              isActive
                                ? "bg-gradient-to-r from-blue-500/20 to-blue-500/5 border border-blue-400/45 shadow-[0_0_20px_rgba(37,99,235,0.25)]"
                                : "bg-black/45 border border-slate-800 hover:border-slate-700 hover:bg-slate-900/80"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span
                                className={`w-8 h-8 rounded-full text-[13px] font-bold flex items-center justify-center flex-shrink-0 ${
                                  isActive
                                    ? "bg-blue-500 text-white "
                                    : row.isChanged
                                    ? "bg-blue-500/90 text-white"
                                    : "bg-slate-700 text-slate-100"
                                }`}
                              >
                                {row.index + 1}
                              </span>
                              <span
                                className={`text-[13px] leading-tight flex-1 min-w-0 truncate font-medium ${
                                  isActive ? "text-white" : "text-slate-100"
                                }`}
                              >
                                {title}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                  </div>

                  <div className="px-4 py-3 border-t border-slate-700/40 flex-shrink-0 bg-slate-900/30">
                    <p className="text-[11px] text-slate-500">
                      <span className="text-blue-300 font-semibold">
                        {stepsWithChanges.length}
                      </span>{" "}
                      of {allStepRows.length} steps changed
                    </p>
                  </div>
                </aside>

                {/* Main content */}
                <div className="flex flex-1 flex-col overflow-hidden">
                  {/* Toolbar */}
                  <div className="px-4 py-2 border-b border-slate-700/40 bg-slate-900/30 flex items-center justify-end flex-shrink-0">
                    <div className="flex gap-0.5 bg-slate-800/60 rounded-lg p-0.5">
                      {(["split", "unified"] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setViewMode(mode)}
                          className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all capitalize ${
                            viewMode === mode
                              ? "bg-slate-600/80 text-slate-100"
                              : "text-slate-500 hover:text-slate-300"
                          }`}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Step list */}
                  <div
                    ref={mainScrollRef}
                    className="flex-1 overflow-y-auto p-4 space-y-3"
                  >
                    {/* Change summary */}
                    {stepsWithChanges.length > 0 && (() => {
                      const added = stepsWithChanges.filter(({ baseStep }) => !baseStep).length;
                      const removed = stepsWithChanges.filter(({ compareStep }) => !compareStep).length;
                      const modified = stepsWithChanges.length - added - removed;
                      const parts: string[] = [];
                      if (modified > 0) parts.push(`${modified} step${modified > 1 ? "s" : ""} modified`);
                      if (added > 0) parts.push(`${added} step${added > 1 ? "s" : ""} added`);
                      if (removed > 0) parts.push(`${removed} step${removed > 1 ? "s" : ""} removed`);
                      return (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40 text-sm text-slate-300">
                          <span className="text-blue-400">⊙</span>
                          <span>{parts.join(" · ")}</span>
                        </div>
                      );
                    })()}

                    {visibleStepRows.length === 0 ? (
                      <div className="rounded-xl border border-slate-700/40 bg-slate-800/20 p-6 text-center">
                        <p className="text-slate-400 text-sm">
                          No differences between these versions.
                        </p>
                      </div>
                    ) : (
                      visibleStepRows.map((row) => (
                        <StepCard
                          key={row.index}
                          row={row}
                          viewMode={viewMode}
                          isActive={activeStepIndex === row.index}
                          onSelect={() => setActiveStepIndex(row.index)}
                          imageUrls={imageUrls}
                          baseLabel={baseLabel}
                          compareLabel={compareLabel}
                          cardRef={(el) => {
                            stepCardRefs.current[row.index] = el;
                          }}
                        />
                      ))
                    )}
                    <div className="h-4" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="rounded-xl border border-slate-700/40 bg-slate-800/20 px-8 py-6 text-center max-w-sm">
                  <p className="text-slate-400 text-sm">
                    Select a <strong className="text-amber-400">From</strong> and{" "}
                    <strong className="text-emerald-400">To</strong> version above to see the
                    differences.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SOPVersions;
